/**
 * Cookie notice: shows the theme's own consent card and passes the answer to
 * Shopify's Customer Privacy API, so apps and pixels follow it. The answer is
 * also kept in this browser, so the card stays away where Shopify cannot
 * store its consent cookie (a preview address) and the answer is passed on
 * again on the next visit.
 */
const STORAGE_KEY = 'sda-cookie-choice';
const KEEP_FOR = 365 * 24 * 60 * 60 * 1000;

function storedChoice() {
  try {
    const choice = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return choice && Date.now() - choice.at < KEEP_FOR ? choice : null;
  } catch {
    return null;
  }
}

function storeChoice(choice) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...choice, at: Date.now() }));
  } catch {
    // Without storage the card simply asks again next time.
  }
}

class CookieNotice extends HTMLElement {
  #controller = new AbortController();
  #box;
  #privacy;

  async connectedCallback() {
    const { signal } = this.#controller;
    this.#box = this.querySelector('.sda-cookie');

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.querySelector('form').addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    document.addEventListener('click', (event) => this.#onSettingsLink(event), { signal, capture: true });

    this.#privacy = await loadCustomerPrivacy();
    if (!this.#privacy?.shouldShowBanner()) return;

    const choice = storedChoice();
    if (choice) this.#pass(choice);
    else this.open();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {{ preferences?: boolean, focus?: boolean }} [options] */
  open({ preferences = false, focus = false } = {}) {
    const consent = this.#privacy?.currentVisitorConsent() ?? {};
    const choice = storedChoice() ?? {};
    for (const input of this.querySelectorAll('input[name]:not(:disabled)')) {
      input.checked = consent[input.name] === 'yes' || choice[input.name] === true;
    }

    this.#box.classList.toggle('is-prefs', preferences);
    this.#box.hidden = false;
    if (focus) this.#box.querySelector(preferences ? 'input:not(:disabled)' : '.sda-cookie__title').focus();
  }

  #save(choice) {
    this.#box.hidden = true;
    storeChoice(choice);
    this.#pass(choice);
  }

  #pass({ analytics, marketing }) {
    this.#privacy?.setTrackingConsent({ analytics, marketing, preferences: analytics, sale_of_data: marketing }, () => {});
  }

  #onClick(event) {
    switch (event.target.closest('[data-cookie]')?.dataset.cookie) {
      case 'accept':
        return this.#save({ analytics: true, marketing: true });
      case 'refuse':
        return this.#save({ analytics: false, marketing: false });
      case 'choose':
        return this.open({ preferences: true, focus: true });
      case 'back':
        return this.#box.classList.remove('is-prefs');
    }
  }

  #onSubmit(event) {
    event.preventDefault();
    const data = new FormData(event.target);
    this.#save({ analytics: data.has('analytics'), marketing: data.has('marketing') });
  }

  #onSettingsLink(event) {
    if (!event.target.closest?.('[data-cookie-settings]')) return;
    event.preventDefault();
    this.open({ preferences: true, focus: true });
  }
}

/** Resolves with Shopify's Customer Privacy API, or undefined when it cannot load. */
function loadCustomerPrivacy() {
  return new Promise((resolve) => {
    if (!window.Shopify?.loadFeatures) return resolve(undefined);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) =>
      resolve(error ? undefined : window.Shopify.customerPrivacy)
    );
  });
}

customElements.define('cookie-notice', CookieNotice);
